/**
 * KnowledgeGraphPanel — live read-out of the in-memory graph.
 *
 * Top: headline stats (files / tests / symbols / specs / edges,
 *      with dangling-edge count badged red when non-zero).
 * Middle: sortable list of nodes grouped by kind. Click a node to
 *         expand a panel showing its outgoing + incoming edges and a
 *         recursive "related" subtree (depth-capped via findRelatedNodes).
 * Refreshes automatically on every store notification.
 */

import { useMemo, useState } from 'react';
import { useKnowledgeGraph, findRelatedNodes } from './useKnowledgeGraph';
import { KG_EDGE_LABEL } from '@gotti/sandbox-ai-v2';
import { KnowledgeGraphCanvas } from './KnowledgeGraphCanvas';
import type { KGEdge, KGNode, KGNodeKind } from '@gotti/sandbox-ai-v2';

type View = 'list' | 'graph';

const KIND_ORDER: KGNodeKind[] = ['spec', 'file', 'test', 'symbol', 'runbook', 'asset', 'api'];

const KIND_LABEL: Record<KGNodeKind, string> = {
  spec: 'Specs', file: 'Files', test: 'Tests', symbol: 'Symbols', runbook: 'Runbooks', asset: 'Assets', api: 'API',
};

const KIND_PILL_CLASS: Record<KGNodeKind, string> = {
  spec: 'kg-pill-spec', file: 'kg-pill-file', test: 'kg-pill-test', symbol: 'kg-pill-symbol',
  runbook: 'kg-pill-runbook', asset: 'kg-pill-asset', api: 'kg-pill-api',
};

export function KnowledgeGraphPanel({ onClose }: { onClose?: () => void }) {
  const snap = useKnowledgeGraph();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [includeExternal, setIncludeExternal] = useState(false);
  const [view, setView] = useState<View>('graph');

  const grouped = useMemo(() => {
    const groups: Record<KGNodeKind, KGNode[]> = {
      spec: [], file: [], test: [], symbol: [], runbook: [], asset: [], api: [],
    };
    const f = filter.trim().toLowerCase();
    for (const n of snap.nodes) {
      if (!includeExternal && n.id.startsWith('external:')) continue;
      if (f && !n.label.toLowerCase().includes(f) && !n.id.toLowerCase().includes(f)) continue;
      groups[n.kind].push(n);
    }
    for (const k of KIND_ORDER) {
      groups[k].sort((a, b) => a.label.localeCompare(b.label));
    }
    return groups;
  }, [snap, filter, includeExternal]);

  const selected = selectedId ? snap.nodes.find(n => n.id === selectedId) ?? null : null;

  return (
    <div className="kg-panel" data-testid="kg-panel">
      <div className="kg-header">
        <span className="kg-title">Knowledge Graph</span>
        <div className="kg-stats" data-testid="kg-stats">
          <KGStat label="files" value={snap.stats.files} />
          <KGStat label="tests" value={snap.stats.tests} />
          <KGStat label="symbols" value={snap.stats.symbols} />
          <KGStat label="specs" value={snap.stats.specs} />
          <KGStat label="runbooks" value={snap.stats.runbooks} />
          <KGStat label="assets" value={snap.stats.assets} />
          <KGStat label="api" value={snap.stats.apis} />
          <KGStat label="edges" value={snap.stats.edges} />
          {snap.stats.danglingEdges > 0 && (
            <KGStat label="dangling" value={snap.stats.danglingEdges} danger />
          )}
          <span className="kg-version" title={`Last updated ${snap.lastUpdatedAt}`}>
            v{snap.version}
          </span>
        </div>
        {onClose && (
          <button type="button" className="kg-close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        )}
      </div>

      <div className="kg-toolbar">
        <div className="kg-view-toggle" role="tablist" data-testid="kg-view-toggle">
          <button
            type="button"
            className={`kg-view-btn${view === 'graph' ? ' active' : ''}`}
            onClick={() => setView('graph')}
            role="tab"
            aria-selected={view === 'graph'}
            data-testid="kg-view-graph"
          >
            Graph
          </button>
          <button
            type="button"
            className={`kg-view-btn${view === 'list' ? ' active' : ''}`}
            onClick={() => setView('list')}
            role="tab"
            aria-selected={view === 'list'}
            data-testid="kg-view-list"
          >
            List
          </button>
        </div>
        <input
          className="kg-filter"
          placeholder="Filter…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          data-testid="kg-filter"
        />
        <label className="kg-toggle">
          <input
            type="checkbox"
            checked={includeExternal}
            onChange={e => setIncludeExternal(e.target.checked)}
          />
          external
        </label>
      </div>

      {view === 'graph' && (
        <div className="kg-canvas-body">
          <KnowledgeGraphCanvas
            snapshot={snap}
            selectedId={selectedId}
            onSelect={id => setSelectedId(id === selectedId ? null : id)}
            hideExternal={!includeExternal}
          />
          {selected && (
            <div className="kg-detail kg-detail-side" data-testid="kg-detail">
              <KGDetail node={selected} edges={snap.edges} allNodes={snap.nodes} />
            </div>
          )}
        </div>
      )}

      {view === 'list' && (
      <div className="kg-body">
        <div className="kg-list" data-testid="kg-list">
          {KIND_ORDER.map(kind => {
            const nodes = grouped[kind];
            if (nodes.length === 0) return null;
            return (
              <div key={kind} className="kg-group">
                <div className="kg-group-header">
                  {KIND_LABEL[kind]} <span className="kg-count">{nodes.length}</span>
                </div>
                {nodes.map(n => (
                  <KGNodeRow
                    key={n.id}
                    node={n}
                    selected={n.id === selectedId}
                    onClick={() => setSelectedId(n.id === selectedId ? null : n.id)}
                  />
                ))}
              </div>
            );
          })}
        </div>

        {selected && (
          <div className="kg-detail" data-testid="kg-detail">
            <KGDetail node={selected} edges={snap.edges} allNodes={snap.nodes} />
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function KGStat({ label, value, danger }: { label: string; value: number; danger?: boolean }) {
  return (
    <span className={`kg-stat${danger ? ' danger' : ''}`}>
      <span className="kg-stat-value">{value}</span>
      <span className="kg-stat-label">{label}</span>
    </span>
  );
}

function KGNodeRow({
  node,
  selected,
  onClick,
}: {
  node: KGNode;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`kg-row kg-row-${node.kind}${selected ? ' selected' : ''}`}
      onClick={onClick}
      data-testid="kg-row"
      data-node-id={node.id}
      data-node-kind={node.kind}
      title={node.id}
    >
      <span className={`kg-pill ${KIND_PILL_CLASS[node.kind]}`}>{node.kind}</span>
      <span className="kg-row-label">{node.label}</span>
      {node.kind === 'symbol' && node.filePath && (
        <span className="kg-row-sub">{node.filePath}</span>
      )}
      {node.kind === 'asset' && node.assetSubtype && (
        <span className="kg-row-sub">{node.assetSubtype}</span>
      )}
      {node.kind === 'api' && node.apiSides && node.apiSides.length > 0 && (
        <span className="kg-row-sub">{node.apiSides.join('/')}</span>
      )}
    </button>
  );
}

function KGDetail({
  node,
  edges,
  allNodes,
}: {
  node: KGNode;
  edges: KGEdge[];
  allNodes: KGNode[];
}) {
  const out = edges.filter(e => e.source === node.id);
  const inc = edges.filter(e => e.target === node.id);
  // Recursive related (capped) — depth 3 is enough to surface most
  // useful cascades without blowing up the panel.
  const related = useMemo(() => findRelatedNodes(node.id, 3), [node.id]);
  const labelOf = (id: string) => allNodes.find(n => n.id === id)?.label ?? id;

  return (
    <>
      <div className="kg-detail-header">
        <span className={`kg-pill ${KIND_PILL_CLASS[node.kind]}`}>{node.kind}</span>
        <span className="kg-detail-title">{node.label}</span>
      </div>
      <div className="kg-detail-meta">
        <code>{node.id}</code>
        <span className="kg-detail-meta-sub">indexed {fmtAgo(node.lastIndexedAt)}</span>
      </div>

      <EdgeGroup heading={`Outgoing (${out.length})`} edges={out} side="target" labelOf={labelOf} />
      <EdgeGroup heading={`Incoming (${inc.length})`} edges={inc} side="source" labelOf={labelOf} />

      <div className="kg-related" data-testid="kg-related">
        <div className="kg-related-header">
          Related (≤3 hops) <span className="kg-count">{related.length}</span>
        </div>
        {related.length === 0 ? (
          <div className="kg-empty">no related nodes</div>
        ) : (
          <ul className="kg-related-list">
            {related.map(id => (
              <li key={id} className="kg-related-item" title={id}>
                {labelOf(id)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function EdgeGroup({
  heading,
  edges,
  side,
  labelOf,
}: {
  heading: string;
  edges: KGEdge[];
  side: 'source' | 'target';
  labelOf: (id: string) => string;
}) {
  return (
    <div className="kg-edge-group">
      <div className="kg-edge-group-header">{heading}</div>
      {edges.length === 0 ? (
        <div className="kg-empty">none</div>
      ) : (
        <ul className="kg-edge-list">
          {edges.map(e => (
            <li
              key={e.key}
              className={`kg-edge kg-edge-${e.status}`}
              data-testid="kg-edge"
            >
              <span className="kg-edge-kind">{KG_EDGE_LABEL[e.kind]}</span>
              <span className="kg-edge-target">{labelOf(e[side])}</span>
              {e.status === 'dangling' && <span className="kg-edge-dangling">dangling</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function fmtAgo(iso: string): string {
  const t = new Date(iso).getTime();
  if (!t || !Number.isFinite(t)) return '—';
  const sec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  return `${hr}h ago`;
}
